/*
 * File: CustomTab.tsx
 * Project: a-a
 * File Created: Saturday, 9th November 2024 8:30:03 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Monday, 28th July 2025 2:31:48 pm
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2024 Mutual Mobile, Mutual Mobile
 */
import React from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import zeplinColor from '../../../theme/Color';
// import { useTheme } from '@mui/material/styles';

interface StyledTabsProps {
  children?: React.ReactNode;
  value: number;
  onChange: (event: React.SyntheticEvent, newValue: number) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  dir?: string;
  index: number;
  value: number;
}

interface customTabProps {
  id: number;
  label: string;
  Component: React.ReactNode;
  isHidden: boolean;
}

interface IProps {
  tabs: customTabProps[];
  //   onTabChange: any;
  defaultTab?: number;
  handleChange: (event: React.SyntheticEvent, newValue: number) => void;
  value: number;
}

const StyledTabs = styled((props: StyledTabsProps) => (
  <Tabs {...props} TabIndicatorProps={{ children: <span className='MuiTabs-indicatorSpan' /> }} />
))({
  marginBottom: '0px',
  '@media (min-width:1700px)': {
    marginBottom: '0px',
  },
  '& .MuiTabs-flexContainer': {
    display: 'flex',
    gap: '8px',
  },
  '& .MuiTabs-indicator': {
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    width: 0,
  },
  '& .MuiTabs-indicatorSpan': {
    maxWidth: 0,
    width: 0,
    backgroundColor: 'none',
  },
  '& .MuiTabs-root': {
    minHeight: '32px', // Change to your desired height
  },
  // Also add this for the specific element that might be controlling height
  '&.MuiTabs-root': {
    minHeight: '32px', // Change to your desired height
  },
});

interface StyledTabProps {
  label: string;
}

const StyledTab = styled((props: StyledTabProps) => <Tab disableRipple {...props} />)(
  ({ theme }) => ({
    textTransform: 'none',
    borderRadius: '50px',
    minHeight: '0px',
    backgroundColor: zeplinColor.Neutral3,
    color: zeplinColor.BasicBlack,
    padding: '12px 19px 12px 19px',
    fontSize: '12px',
    '@media (min-width:1700px)': {
      padding: '15px 24px 15px 24px',
      // minWidth: '160px', // Larger min width for very large screens
      fontSize: '14px', // Maintain 14px font size for large screens
    },
    fontWeight: 400,
    '&.MuiChip-root': {
      // height: { xs: '32px', xl: '40px' },
      // padding: { xs: '10px 3px 10px 7px', xl: '15px 24px 15px 24px' },
    },

    '&:not(.Mui-selected):hover': {
      backgroundColor: zeplinColor.Neutral4,
    },
    '&.Mui-selected': {
      color: zeplinColor.Neutral1,
      backgroundColor: zeplinColor.BasicBlack,
    },
    // '&.Mui-focusVisible': {
    //   backgroundColor: zeplinColor.BasicBlack,
    // },
  }),
);

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
      style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
    >
      {value === index && (
        <Box sx={{ p: '0px', flex: 1, display: 'flex', flexDirection: 'column' }}>{children}</Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `full-width-tab-${index}`,
    'aria-controls': `full-width-tabpanel-${index}`,
  };
}

function CustomTab({ tabs, defaultTab, handleChange, value }: IProps) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        width: '100%',
        // flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: '12px', xl: '16px' },
        position: 'relative',
      }}
    >
      <StyledTabs value={value} onChange={handleChange} aria-label='styled tabs example'>
        {tabs.map((option) => (
          <StyledTab label={option.label} key={option.id} {...a11yProps(option.id)} />
        ))}
      </StyledTabs>
      {tabs.map(
        ({ Component }, i: number) =>
          !tabs[i].isHidden && (
            <TabPanel value={value} index={i} key={i}>
              {Component}
            </TabPanel>
          ),
      )}
    </Box>
  );
}

export default CustomTab;
